/**
 * Block 50/50 with Star Image — front-end video behaviour.
 *
 * The hosted MP4 autoplays muted in a loop behind the poster image, a loader
 * shows while it buffers, a control lets the user pause/resume, and playback
 * stops while the block is scrolled out of view. Mirrors the intro-star-hero
 * ambient video pattern.
 *
 * @see src/blocks/intro-star-hero/view.js
 */

const PLAYING_CLASS = 'is-playing';
const LOADING_CLASS = 'is-loading';
const PAUSED_CLASS = 'is-paused';

/**
 * Whether the visitor asked the OS to minimise motion.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Wire up the video: autoplay, loader and pause/resume.
 *
 * @param {HTMLElement} block The block root element.
 */
const initVideo = ( block ) => {

	const video = block.querySelector( '.b-fifty-fifty-with-star-image__video' );
	const frame = block.querySelector( '.b-fifty-fifty-with-star-image__media-frame' );

	if ( ! video || ! frame ) {

		return;

	}

	const toggle = block.querySelector( '.b-fifty-fifty-with-star-image__toggle' );

	// Tracks an explicit user pause so the viewport observer never resumes
	// against the user's intent.
	let userPaused = false;

	/**
	 * Reveal the video once it is actually playing and drop the loader.
	 */
	const onPlaying = () => {

		frame.classList.remove( LOADING_CLASS );
		frame.classList.add( PLAYING_CLASS );
		frame.classList.remove( PAUSED_CLASS );

	};

	/**
	 * Start playback, showing the loader until the first frame renders.
	 */
	const play = () => {

		frame.classList.remove( PAUSED_CLASS );

		if ( ! frame.classList.contains( PLAYING_CLASS ) ) {

			frame.classList.add( LOADING_CLASS );

		}

		const promise = video.play();

		if ( promise && typeof promise.catch === 'function' ) {

			promise.catch( () => frame.classList.remove( LOADING_CLASS ) );

		}

	};

	/**
	 * Pause playback and surface the play control.
	 */
	const pause = () => {

		video.pause();
		frame.classList.remove( LOADING_CLASS );
		frame.classList.add( PAUSED_CLASS );

	};

	video.addEventListener( 'playing', onPlaying );

	if ( toggle ) {

		toggle.addEventListener( 'click', () => {

			if ( video.paused ) {

				userPaused = false;
				play();

			} else {

				userPaused = true;
				pause();

			}

			toggle.setAttribute(
				'aria-label',
				video.paused ? 'Play video' : 'Pause video',
			);

		} );

	}

	// Reduced motion: never autoplay — leave the poster and offer the control.
	if ( prefersReducedMotion() ) {

		frame.classList.add( PAUSED_CLASS );
		userPaused = true;

	} else if ( 'IntersectionObserver' in window ) {

		// Only play while the block is on screen; spare the decoder otherwise.
		const observer = new IntersectionObserver(
			( entries ) => {

				entries.forEach( ( entry ) => {

					if ( entry.isIntersecting ) {

						if ( ! userPaused ) {

							play();

						}

					} else if ( ! video.paused ) {

						video.pause();

					}

				} );

			},
			{ threshold: 0.1 },
		);

		observer.observe( block );

	} else {

		play();

	}

};

document.querySelectorAll( '.b-fifty-fifty-with-star-image.is-video' ).forEach( initVideo );
